import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { HotelCardContext } from 'components/HotelCardContext';
import { useOfferContainer } from 'hooks/useOfferContainer';
import { getCurrencyCode, getLocalTaxesIncluded, getTaxesIncluded } from 'modules/meta/selectors';
import { getIsOffersPending, getIsSearchPending, getOfferEntity, getShowTotalPrices, getSplitBookingDetails } from 'modules/sapiSearch/selectors';
import { getIsOTAMode } from 'modules/search/selectors';
import { Button } from '@daedalus/atlas/Button';
import { Box } from '@daedalus/atlas/helpers/Box';
import { Icon } from '@daedalus/atlas/Icon';
import { SkeletonLoader } from '@daedalus/atlas/SkeletonLoader';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import getIsBofhOffer from '@daedalus/core/src/offer/business/isBofhOffer';
import { useVioTag } from '@daedalus/core/src/tag';
import { VioDealTag } from '@daedalus/shared/src/privateDeal/VioDealTag';
import { Price } from '../Price';
const Wrapper = /*#__PURE__*/styled('div')({
  name: "Wrapper",
  class: "w1ymd44i",
  propsAsIs: false
});
const CTAButton = () => {
  return <Button size="lg" iconEnd={<Box hasMirror>
          <Icon name="ChevronRight" />
        </Box>}>
      <FormattedMessageWrapper id="viewDeals" defaultMessage="View deals" />
    </Button>;
};
const Loading = () => {
  return <Wrapper>
      <SkeletonLoader height={32} width={70} />
      <SkeletonLoader height={12} width={116} />
      <SkeletonLoader height={42} width={137} />
    </Wrapper>;
};
export const CheapestOffer: React.FC = () => {
  const currencyCode = useSelector(getCurrencyCode);
  const {
    brandCode
  } = useBrand();
  const {
    hotelId
  } = useContext(HotelCardContext);
  const isOTAMode = useSelector(getIsOTAMode);
  const offerEntity = useSelector(state => getOfferEntity(state, hotelId));
  const isSearchPending = useSelector(getIsSearchPending);
  const isOffersPending = useSelector(getIsOffersPending);
  const isLoading = isSearchPending || isOffersPending;
  const includeTaxes = useSelector(getTaxesIncluded);
  const includeLocalTaxes = useSelector(getLocalTaxesIncluded);
  const showTotalPrices = useSelector(getShowTotalPrices);
  const {
    topOffers,
    anchorPrice,
    anchorPriceTotal
  } = useOfferContainer({
    offerEntity,
    hotelId,
    isOTAMode
  });
  const offer = topOffers?.[0];
  const isVioOffer = getIsBofhOffer(offer);
  const {
    showSplitBookingLoader,
    hasSplitBookingOffer,
    splitBookingType,
    splitBookingPosition
  } = useSelector(state => getSplitBookingDetails(state, hotelId));
  const hasSBOffer = useMemo(() => {
    const hasSingleSBOffer = hasSplitBookingOffer && splitBookingType === 'single_flow';
    const hasSplitSBOffer = hasSplitBookingOffer && splitBookingType === 'split_flow';

    // In case of no offers, we should show any split booking offer available
    if (!topOffers.length) return hasSplitSBOffer || hasSingleSBOffer;

    // In case we have a single flow SB offer and it is promoted to the first position, we show it as primary offer
    return hasSingleSBOffer && splitBookingPosition === 0;
  }, [hasSplitBookingOffer, splitBookingType, topOffers, splitBookingPosition]);
  const {
    isVioTagVisible,
    savingType,
    savingPrice,
    savingPercentage
  } = useVioTag({
    price: showTotalPrices ? offer?.totalRate : offer?.nightlyRate,
    anchorPrice: showTotalPrices ? anchorPriceTotal : anchorPrice,
    currencyCode,
    isVioOffer,
    brandCode
  });
  if (!offer && !hasSBOffer) {
    if (isLoading || showSplitBookingLoader) return <Loading />;

    // If case of unavailability, we don't show any offer
    return null;
  }
  if (hasSBOffer) {
    return <Wrapper>
        <Price hotelId={hotelId} includeLocalTaxes={includeLocalTaxes} includeTaxes={includeTaxes} isSplitBooking showTotalPrices={showTotalPrices} noSpecialStyle />

        <CTAButton />
      </Wrapper>;
  }
  return <Wrapper>
      {isVioTagVisible && <VioDealTag message={{
      id: 'vioLabelFinal',
      defaultMessage: 'Only on Vio'
    }} savingPercentage={savingPercentage} savingPrice={savingPrice} currencyCode={currencyCode} savingType={savingType} />}
      <Price hotelId={hotelId} includeLocalTaxes={includeLocalTaxes} includeTaxes={includeTaxes} offer={offer} showTotalPrices={showTotalPrices} noSpecialStyle={!isVioTagVisible}
    // Only necessary for the price to be displayed in purple
    isPrivateDeal={isVioTagVisible} />
      <CTAButton />
    </Wrapper>;
};

require("./CheapestOffer.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./CheapestOffer.tsx");